import { useThree, useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { useState, useEffect, useRef, useCallback } from "react";
import { usePlacedModelsStore, useUserInterfaceStore, useMusicPlaybackStore, useModelStore, useRoomStore } from "../../store";
import OutlineEffect from "./OutlineEffect";
import { useNavigate } from "react-router-dom";
import FEATURED_ROOM_SLUGS from "../../config/featuredRooms.json";
import { REACT_APP_STAGE } from "../../config/environment";

export const getFeaturedRoomSlugsForDoor = () => {
  const productionSlugs = FEATURED_ROOM_SLUGS || [];
  if (REACT_APP_STAGE === 'dev') {
    return [...productionSlugs, 'dev-audio-room', 'dev-beentrappers', 'dev-blakeshawn', 'dev-electric-death', 'dev-itcouldbeworsenyc', 'dev-light-hub-1', 'dev-unnamed'];
  }
  return productionSlugs;
};

export default function CenterRaycaster({ onHover, onClick, isMobile = false }) {
  const { camera, scene, gl } = useThree();
  const raycaster = new THREE.Raycaster();
  const mouse = new THREE.Vector2(0, 0);

  const [hoveredObject, setHoveredObject] = useState();
  const navigate = useNavigate();
  const setShowLinkIndicators = useUserInterfaceStore((s) => s.setShowLinkIndicators);
  const showLinkIndicators = useUserInterfaceStore((s) => s.showLinkIndicators);

  // Hide blue link indicators when green hover outline is showing
  useEffect(() => {
    setShowLinkIndicators(!hoveredObject);
  }, [hoveredObject, setShowLinkIndicators]);

  // Collect interactable 3D object meshes for blue outline
  const modelCount = useModelStore((s) => s.models.length);
  const placedModels = usePlacedModelsStore((s) => s.placedModels);
  const [interactableMeshes, setInteractableMeshes] = useState([]);

  useEffect(() => {
    if (!showLinkIndicators) { setInteractableMeshes([]); return; }

    const interactableIds = new Set(
      placedModels.filter(m => m.link || m.hoverText || m.music).map(m => m.id)
    );
    if (interactableIds.size === 0) { setInteractableMeshes([]); return; }

    const meshes = [];
    scene.traverse((obj) => {
      if (obj.userData?.isGroupRoot && interactableIds.has(obj.userData.sourceId)) {
        obj.traverse((child) => { if (child.isMesh) meshes.push(child); });
      }
    });
    setInteractableMeshes(meshes);
  }, [scene, modelCount, placedModels, showLinkIndicators]);

  // Helper to raycast and find linkable objects
  const findLinkableObject = useCallback((screenX, screenY) => {
    const rect = gl.domElement.getBoundingClientRect();
    const x = ((screenX - rect.left) / rect.width) * 2 - 1;
    const y = -((screenY - rect.top) / rect.height) * 2 + 1;

    raycaster.setFromCamera(new THREE.Vector2(x, y), camera);
    const intersects = raycaster.intersectObjects(scene.children, true);

    if (intersects.length > 0) {
      const newHover = getGroupRoot(intersects[0].object);

      if (newHover?.userData?.sourceId) {
        const modelId = newHover?.userData?.sourceId;
        if (modelId) {
          const model = usePlacedModelsStore.getState().getModelById(modelId);
          if (model?.link || model?.hoverText || model?.music) {
            const meshes = [];
            newHover.traverse((e) => {
              if (e.isMesh) meshes.push(e);
            });
            return {
              meshes,
              link: model.link,
              hoverText: model.hoverText,
              music: model.music,
              musicModelId: model.music ? modelId : null,
              type: model.type,
              modelId,
            };
          }
        }
      } else if (newHover?.userData?.link || newHover?.userData?.hoverText || newHover?.userData?.decorationType === "youtube" || newHover?.userData?.decorationType === "spotify" || newHover?.userData?.decorationType === "twitch") {
        const meshes = [];
        newHover.traverse((e) => {
          if (e.isMesh) meshes.push(e);
        });
        return {
          ...newHover.userData,
          meshes,
          link: newHover.userData.link,
          hoverText: newHover.userData.hoverText,
        };
      }
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [camera, scene, gl]);

  useFrame(() => {
    if (hoveredObject) {
      onHover?.(hoveredObject);
    }

    // Desktop: require pointer lock for center raycasting
    // Mobile: always raycast from center (no pointer lock on mobile)
    if (!isMobile && !document.pointerLockElement) {
      if (hoveredObject) setHoveredObject(null);
      return;
    }
    raycaster.setFromCamera(mouse, camera);

    const intersects = raycaster.intersectObjects(scene.children, true);

    if (intersects.length > 0) {
      const newHover = getGroupRoot(intersects[0].object);

      if (hoveredObject !== newHover) {
        if (newHover?.userData?.sourceId) {
          const modelId = newHover?.userData?.sourceId;
          if (modelId) {
            const model = usePlacedModelsStore.getState().getModelById(modelId);
            if (model.link || model.hoverText || model.music) {
              const meshes = [];
              newHover.traverse((e) => {
                if (e.isMesh) meshes.push(e);
              });

              setHoveredObject({
                meshes,
                link: model.link,
                hoverText: model.hoverText,
                music: model.music,
                musicModelId: model.music ? modelId : null,
                type: model.type,
                modelId,
              });
            } else {
              setHoveredObject(null);
            }
          }
        } else {
          if (newHover?.userData.link || newHover?.userData.hoverText || newHover?.userData.decorationType === "youtube" || newHover?.userData.decorationType === "spotify" || newHover?.userData.decorationType === "twitch") {
            const meshes = [];
            newHover.traverse((e) => {
              if (e.isMesh) meshes.push(e);
            });
            setHoveredObject({
              ...newHover.userData,
              meshes,
              link: newHover.userData.link,
              hoverText: newHover.userData.hoverText,
            });
          } else {
            setHoveredObject(null);
          }
        }
      } else {
        setHoveredObject(null);
      }
    } else {
      setHoveredObject(null);
      // onHover?.(null);
    }
  });

  function getGroupRoot(mesh) {
    let parent = mesh;
    while (parent) {
      if (
        parent.userData.isGroupRoot ||
        parent.userData.isDecoration ||
        parent.userData.isDoor
      ) {
        break;
      }
      if (parent.parent) parent = parent.parent;
      else return;
    }
    return parent;
  }

  const skipNextClick = useRef(false);
  useEffect(() => {
    const handlePointerLockChange = () => {
      // When we enter pointer lock, mark the next click to skip
      if (document.pointerLockElement) {
        skipNextClick.current = true;
      }
    };

    const handleClick = (e) => {
      if (skipNextClick.current) {
        skipNextClick.current = false; // skip once
        return;
      }

      // Desktop with pointer lock: use center-hovered object
      // Mobile: raycast from tap position to verify click hit an interactable
      let clickedObject = null;
      if (document.pointerLockElement) {
        // Desktop: click always goes to center-hovered object
        clickedObject = hoveredObject;
      } else if (isMobile) {
        // Mobile: raycast from actual tap position
        clickedObject = findLinkableObject(e.clientX, e.clientY);
      }

      // For interactive iframe decorations (Spotify/YouTube), exit pointer lock
      // so the user can interact directly with the embedded player
      if (clickedObject && (clickedObject.decorationType === "spotify" || clickedObject.decorationType === "youtube" || clickedObject.decorationType === "twitch") && !clickedObject.link) {
        if (document.pointerLockElement) {
          document.exitPointerLock();
        }
        onClick?.(clickedObject);
        return;
      }

      if (clickedObject?.music && clickedObject?.musicModelId) {
        useMusicPlaybackStore.getState().togglePlaying(clickedObject.musicModelId);
        onClick?.(clickedObject);
        return;
      }

      // Plexian avatar interaction: dispatch a custom event so the avatar
      // can swap its bubble to the interactive text. Carries modelId so a
      // room with multiple placed plexians targets only the clicked one.
      if (clickedObject?.type === 'plexian_avatar' && clickedObject?.modelId) {
        window.dispatchEvent(new CustomEvent('plexian-avatar-click', {
          detail: { modelId: clickedObject.modelId },
        }));
        onClick?.(clickedObject);
        return;
      }

      if (clickedObject && clickedObject.link) {
        let link = clickedObject.link.trim();

        if (clickedObject?.isRandomDoor) {
          const currentSlug = window.location.pathname.replace(/^\//, '').replace(/\/$/, '');
          const candidates = getFeaturedRoomSlugsForDoor().filter(s => s !== currentSlug);
          const randomSlug = candidates[Math.floor(Math.random() * candidates.length)];
          navigate("/" + randomSlug);
        } else if (clickedObject?.isDoor) {
          const creatorUsername = useRoomStore.getState().creatorUsername;
          navigate(creatorUsername ? `/u/${creatorUsername}` : "/");
        } else if (link) {
          // Validate and sanitize the URL to prevent open redirect attacks
          if (!/^https?:\/\//i.test(link)) {
            link = "https://" + link;
          }

          // Basic URL validation - must be http/https and not javascript: or data:
          try {
            const url = new URL(link);
            if (url.protocol === 'http:' || url.protocol === 'https:') {
              window.open(link, "_blank", "noopener,noreferrer");
            } else {
              console.warn('Blocked potentially unsafe URL:', link);
            }
          } catch (e) {
            console.warn('Invalid URL format:', link);
          }
        }
        onClick?.(clickedObject);
      }
    };

    document.addEventListener("pointerlockchange", handlePointerLockChange);
    window.addEventListener("click", handleClick);

    return () => {
      document.removeEventListener(
        "pointerlockchange",
        handlePointerLockChange
      );
      window.removeEventListener("click", handleClick);
    };
  }, [hoveredObject, navigate, onClick, isMobile, findLinkableObject]);

  return (
    <>
      {/* {hoveredObject && hoveredObject?.hoverText && (
        <Billboard scale={100} position={hoveredObject.meshes?.[0].position}>
          <Text>{hoveredObject.hoverText}</Text>
        </Billboard>
      )} */}
      <OutlineEffect
        selected={hoveredObject?.meshes}
        interactableMeshes={interactableMeshes}
        hoverText={hoveredObject?.hoverText}
        musicModelId={hoveredObject?.musicModelId}
      />
      ;
    </>
  );
}
